import React, { useState } from "react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GridTable from "cvmgmt-cube-grid-table";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { triggerToast } from "../../../utils/toast";
import { getEnv } from "../../../Constants/env";
import { useIdToken } from "../../../hooks/useAuthSession";
import CustomBreadcrumb from "../../CustomBreadcrumb/CustomBreadcrumb";
import ConfirmModal from "../../Modals/ConfirmModal";
import { adminApprovaDeny } from "../../../services/RequestAccess/RequestAccess";

type DrillLevel = "applications" | "roles" | "users";

interface SelectedApp {
  id: number;
  name: string;
}

interface SelectedRole {
  id: number;
  name: string;
}

const GRID_HEIGHT = "calc(100vh - 30.5rem)";
// Roles/Users levels render a breadcrumb bar above the table that the
// Applications level doesn't, so they need extra reserved space.
const GRID_HEIGHT_WITH_BREADCRUMB = "calc(100vh - 33.5rem)";

const buildApiConfig = (url: string, idToken: string | null) => ({
  url,
  data: {
    sortingFilter: {
      filter: [],
      sort: [],
      page: { limit: 25, offset: 0 },
    },
  },
  headers: {
    Authorization: idToken,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  documentUrl: "",
});

const ApplicationRoleAccess: React.FC = () => {
  const navigate = useNavigate();
  const idToken = useIdToken();
  const { env } = useSelector((state: any) => state?.createApplication);

  const [level, setLevel] = useState<DrillLevel>("applications");
  const [selectedApp, setSelectedApp] = useState<SelectedApp | null>(null);
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>(null);
  const [refresh, setRefresh] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [revokeData, setRevokeData] = useState<any | null>(null);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);

  const goToApplications = () => {
    setLevel("applications");
    setSelectedApp(null);
    setSelectedRole(null);
  };

  const goToRoles = (app: SelectedApp) => {
    setSelectedApp(app);
    setSelectedRole(null);
    setLevel("roles");
  };

  const goToUsers = (role: SelectedRole) => {
    if (!selectedApp) return;
    setSelectedRole(role);
    setLevel("users");
  };

  const handleRevoke = async (rowData: any) => {
    if (isRevoking) return;
    setIsRevoking(true);
    try {
      const response = await adminApprovaDeny({
        action: "revoked",
        accessRoleList: [
          {
            accessRoleId: rowData?.access_id,
            subrequestId: rowData?.subrequest_id,
            approvedRoleIds: [],
            deniedRoleIds: [],
            comment: modalMessage,
          },
        ],
      });
      if (response?.status?.toLowerCase() === "success") {
        triggerToast("Access is successfully revoked", "success");
      } else {
        triggerToast(
          "Something went wrong! Please try after sometime.",
          "error",
        );
      }
    } catch (error) {
      triggerToast("An error occurred while processing your request.", "error");
    } finally {
      setRefresh((prev) => prev + 1);
      setIsModalOpen(false);
      setModalMessage("");
      setIsRevoking(false);
    }
  };

  const breadcrumbItems: any[] = [
    {
      label: "Applications",
      icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
      onClick: level !== "applications" ? goToApplications : undefined,
    },
  ];
  if (level !== "applications" && selectedApp) {
    breadcrumbItems.push({
      label: selectedApp.name,
      onClick: level === "users" ? () => goToRoles(selectedApp) : undefined,
    });
  }
  if (level === "users" && selectedRole) {
    breadcrumbItems.push({ label: selectedRole.name });
  }

  return (
    <>
      {level !== "applications" && (
        <div style={{ marginBottom: "16px" }}>
          <CustomBreadcrumb items={breadcrumbItems} />
        </div>
      )}
      {level === "applications" && idToken !== null && env && (
        <GridTable
          title="Applications"
          style={{ height: GRID_HEIGHT }}
          customPayload={true}
          isCustomizeColumns={false}
          isPagination={true}
          apiConfig={buildApiConfig(
            `${getEnv()?.baseURL}/user/appRoleAccess?env=${env}`,
            idToken,
          )}
          onRowClick={(_: any, row: any) => {
            const rowData = row?.original;
            const appId = rowData?.app_id;
            if (appId === undefined || appId === null) {
              triggerToast("Application Id is missing.", "error");
              return;
            }
            goToRoles({ id: appId, name: rowData?.application });
          }}
          showLoader={false}
        />
      )}
      {level === "roles" && selectedApp && idToken !== null && env && (
        <GridTable
          title="Roles"
          style={{ height: GRID_HEIGHT_WITH_BREADCRUMB }}
          customPayload={true}
          isCustomizeColumns={false}
          isPagination={true}
          apiConfig={buildApiConfig(
            `${getEnv()?.baseURL}/user/appRoleAccess?app_id=${selectedApp.id}&env=${env}`,
            idToken,
          )}
          onRowClick={(_: any, row: any) => {
            const rowData = row?.original;
            const roleId = rowData?.role_id;
            if (roleId === undefined || roleId === null) {
              triggerToast("Role Id is missing.", "error");
              return;
            }
            goToUsers({ id: roleId, name: rowData?.role_name });
          }}
          showLoader={false}
        />
      )}
      {level === "users" && selectedRole && idToken !== null && env && (
        <>
          <GridTable
            title="Users"
            refresh={refresh}
            style={{ height: GRID_HEIGHT_WITH_BREADCRUMB }}
            customPayload={true}
            isCustomizeColumns={false}
            isPagination={true}
            apiConfig={buildApiConfig(
              `${getEnv()?.baseURL}/user/appRoleAccess?role_id=${selectedRole.id}&env=${env}`,
              idToken,
            )}
            cellActions={[
              {
                event: "View Access",
                callback: (_event: any, data: any) => {
                  const rowData = data?.row?.original;
                  navigate(`/user-access/${rowData?.workday_id}`, {
                    state: {
                      userId: rowData?.user_id,
                      roleId: selectedRole?.id,
                      workdayID: rowData?.workday_id,
                      role: selectedRole?.name,
                      accessStatus: "Active",
                      userName: rowData?.user_name,
                    },
                  });
                },
                icon: false,
              },
              {
                event: "Revoke",
                callback: (_event: any, data: any) => {
                  setRevokeData(data?.row?.original ?? null);
                  setIsRevoking(false);
                  setIsModalOpen(true);
                },
                icon: false,
              },
            ]}
            showLoader={false}
          />
          <ConfirmModal
            value={modalMessage}
            limitmessage={"Reason for Revoking"}
            modalMessage={
              "Please provide a short reason for revoking access in the field below."
            }
            isOpen={isModalOpen}
            headLabel={"Revoke Access"}
            handleClose={() => {
              setIsModalOpen(false);
              setModalMessage("");
            }}
            handleConfirm={() => handleRevoke(revokeData)}
            cancelButtonLabel={"Cancel"}
            confirmButtonLabel={"Revoke"}
            confirmButtonStyle="danger"
            confirmButtonDisabled={isRevoking}
            handleTextFeild={(value: string) => {
              setModalMessage(value);
            }}
          />
        </>
      )}
    </>
  );
};

export default ApplicationRoleAccess;


